import { inject, watch, onUnmounted } from "vue";
import { useStore } from "vuex";
import { PAGE_APP_SYMBOL } from "_Main/injectionSymbols";
import { FORM_NAMESPACE } from "SharedStores/Form/formStore";
import { PROJECT_FORM_TYPES, PVELEM_SHOW_STORAGE_MUTATION } from "Forms/Components/ProjectForm/projectFormStore";
import { FormulaEvaluator } from "Formula/evaluator";

/**
 * just needs to get the values from the store that this depends on.
 * needs to be sync and more or less mirror _shouldShowStorageEquipment
 */
function _getValuesToWatch($app, showStorageEquipmentFormula, state, getters, $serviceControllers) {
    if (showStorageEquipmentFormula == null) {
        return;
    }
    const clone = JSON.parse(JSON.stringify(showStorageEquipmentFormula));
    const evaluator = new FormulaEvaluator(
        $app,
        clone,
        { rootState: state, rootGetters: getters },
        $serviceControllers
    );
    return evaluator.getReferencedValuesDictionary();
}

async function _shouldShowStorageEquipment($app, showStorageEquipmentFormula, state, getters, $serviceControllers) {
    if (showStorageEquipmentFormula == null) {
        return;
    }
    const clone = JSON.parse(JSON.stringify(showStorageEquipmentFormula));
    const evaluator = new FormulaEvaluator(
        $app,
        clone,
        { rootState: state, rootGetters: getters },
        $serviceControllers
    );
    await evaluator.eval();
    return clone.result.Value === true;
}

/**
 * Composition API composable for PV Storage Visibility management
 * Usage: call pvStorageVisibilityManager inside setup()
 */
export function pvStorageVisibilityManager() {
    const app = inject(PAGE_APP_SYMBOL);
    const store = useStore();

    const unwatchHooks = [];

    function setupWatchers($serviceControllers) {
        unwatchHooks.length = 0;
        const formElements = store.state[FORM_NAMESPACE].FormElements;
        for (const publicId in formElements) {
            if (formElements.hasOwnProperty(publicId)) {
                const element = formElements[publicId];
                if (element.ShowStorageEquipmentFormula) {
                    const unwatchFn = watch(
                        () => {
                            const valuesToWatch = _getValuesToWatch(
                                app,
                                element.ShowStorageEquipmentFormula,
                                store.state,
                                store.getters,
                                $serviceControllers
                            );
                            return { valuesToWatch };
                        },
                        () => watcherCallback(element, $serviceControllers),
                        { deep: true, flush: "sync" }
                    );
                    unwatchHooks.push(unwatchFn);
                }
            }
        }
    }

    async function watcherCallback(element, $serviceControllers) {
        let shouldShowStorageEquipment = false;
        if (element.ShowStorageEquipmentFormula) {
            shouldShowStorageEquipment = await _shouldShowStorageEquipment(
                app,
                element.ShowStorageEquipmentFormula,
                store.state,
                store.getters,
                $serviceControllers
            );
        }
        const elementId = element.PublicId;

        // only trigger downstream events if visibility value actually changed
        const projectElement = store.getters[PROJECT_FORM_TYPES.getters.projectElementFromId](elementId);
        if (projectElement.AuxiliaryModel.pvShowStorageEquipment !== shouldShowStorageEquipment) {
            store.commit(PVELEM_SHOW_STORAGE_MUTATION, { elementId, shouldShowStorageEquipment });
        }
    }

    function unhook() {
        unwatchHooks.forEach((unwatch) => unwatch());
        unwatchHooks.length = 0;
    }

    // Clean up on unmount
    onUnmounted(() => {
        unhook();
    });

    return {
        setupWatchers,
        unhook
    };
}
